import { useEffect, useState } from 'react';
import { MoonIcon, SunIcon } from 'lucide-react';
import { aimdoc } from '@aimdoc/sdk';

export function useTheme() {
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) {
        return savedTheme === 'dark';
      }
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (!localStorage.getItem('theme')) {
        setIsDark(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const root = window.document.documentElement;
    if (isDark) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
    // Keep our own in-app Aimdoc agent on the dashboard's theme. Targeted at
    // the dogfood agent so the agent-builder preview widget (which lives in
    // this same window) keeps following the customer's dark-mode settings.
    const dogfoodAgentId = import.meta.env.VITE_PUBLIC_AGENT_ID;
    if (dogfoodAgentId) {
      aimdoc.setDisplayOptions({ theme: isDark ? 'dark' : 'light' }, { agentId: dogfoodAgentId });
    }
  }, [isDark]);

  return { isDark, setIsDark };
}

export default function ThemeSwitcher() {
  const { isDark, setIsDark } = useTheme();

  return (
    <button
      onClick={() => setIsDark(!isDark)}
      className="flex flex-row justify-between items-center h-full w-full px-4 py-2 text-sm border rounded-lg transition duration-200"
    >
      <div className="flex flex-col w-full justify-between">
        <div className='text-sm text-start'>
          Theme
        </div>
      </div>
      {isDark ? <MoonIcon /> : <SunIcon />}
    </button>
  );
}